<template>
  <div class="calendar" :class="`calendar--${size}`">
    <CalendarHeader
      :size="size"
      :monthName="headerDisplay"
      :selectedYear="selectedYear"
      @navigate-month="navigateYear"
      @toggle-year-selector="toggleYearSelector"
    />
    <div class="calendar-wrapper">
      <transition name="fade" mode="out-in">
        <WeeksGrid
          v-if="!showingYears"
          :class="clsx('weeks-grid', { 'weeks-grid__alignment': gridFixedHeight })"
          :show="!showingYears"
          :size="size"
          :currentDate="currentDate"
          :selectedYear="selectedYear"
          :range="range"
          :startDate="startDate"
          :endDate="endDate"
          :maxRangeLimit="maxRangeWeeks"
          :limitUnit="'week'"
          :selectingRange="selectingRange"
          @select-week="handleWeekSelection"
          key="weeks-grid"
        />
        <YearsGrid
          v-else
          :class="clsx('years-grid', { 'years-grid__alignment': gridFixedHeight })"
          :show="showingYears"
          :size="size"
          :yearOptions="yearOptions"
          :selectedYear="selectedYear"
          :range="range"
          :startDate="startDate"
          :endDate="endDate"
          :maxRangeLimit="maxRangeWeeks"
          :limitUnit="'week'"
          :selectingRange="selectingRange"
          @select-year="handleYearSelection"
          key="years-grid"
        />
      </transition>
    </div>
  </div>
</template>

<script>
import clsx from 'clsx';
import { moment } from '../../lib';
import CalendarHeader from '../calendar-header';
import WeeksGrid from '../weeks-grid';
import YearsGrid from '../years-grid';

export default {
  mixins: [ moment ],
  components: { CalendarHeader, WeeksGrid, YearsGrid },
  props: {
    value: {
      type: [Date, Array],
      default: null,
    },
    range: {
      type: Boolean,
      default: false,
    },
    maxRangeWeeks: {
      type: Number,
      default: null,
      validator: (value) => value === null || value > 0,
    },
    startYear: {
      type: Number,
      default: null,
    },
    endYear: {
      type: Number,
      default: null,
    },
    size: {
      type: String,
      default: 'normal',
      validator: (value) => ['normal', 'small'].includes(value),
    },
    gridFixedHeight: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      currentDate: this.getMoment(),
      selectedWeek: null,
      startDate: null,
      endDate: null,
      selectingRange: false,
      showingYears: false,
    };
  },
  computed: {
    selectedYear() {
      return this.getYearMoment(this.currentDate);
    },
    yearOptions() {
      const years = [];
      let minYear;
      let maxYear;
      if (this.startYear !== null && this.endYear !== null) {
        minYear = this.startYear;
        maxYear = this.endYear;
      } else {
        const baseYear = this.getYearMoment(this.currentDate);
        minYear = baseYear - 20;
        maxYear = baseYear + 20;
      }
      for (let year = minYear; year <= maxYear; year++) {
        years.push(year);
      }
      return years;
    },
    headerDisplay() {
      const currentCalendarSelectedYear = this.selectedYear;

      if (this.range) {
        if (this.startDate && this.endDate) {
          const startYear = this.getISOWeekYearMoment(this.startDate);
          const endYear = this.getISOWeekYearMoment(this.endDate);
          const startWeekNumber = this.getWeekMoment(this.startDate);
          const endWeekNumber = this.getWeekMoment(this.endDate);

          if (startYear === endYear) {
            return `Тиждень ${startWeekNumber} - Тиждень ${endWeekNumber}${startYear !== currentCalendarSelectedYear ? ` ${startYear}` : ''}`;
          } else {
            let startDisplay = `Тиждень ${startWeekNumber}`;
            if (startYear !== currentCalendarSelectedYear) {
              startDisplay += ` ${startYear}`;
            }

            let endDisplay = `Тиждень ${endWeekNumber}`;
            if (endYear !== currentCalendarSelectedYear) {
              endDisplay += ` ${endYear}`;
            }
            return `${startDisplay} - ${endDisplay}`;
          }
        } else if (this.startDate && this.selectingRange) {
          const startYear = this.getISOWeekYearMoment(this.startDate);
          const startWeekNumber = this.getWeekMoment(this.startDate);
          return `Тиждень ${startWeekNumber}${startYear !== currentCalendarSelectedYear ? ` ${startYear}` : ''} -`;
        } else {
          return 'Тиждень';
        }
      } else {
        const displayDate = this.selectedWeek || this.currentDate;
        const displayYear = this.getISOWeekYearMoment(displayDate);
        const displayWeekNumber = this.getWeekMoment(displayDate);
        return `Тиждень ${displayWeekNumber}${displayYear !== currentCalendarSelectedYear ? ` ${displayYear}` : ''}`;
      }
    },
  },
  methods: {
    clsx,
    navigateYear(direction) {
      this.currentDate = this.addMoment(this.currentDate, direction, 'year');
      this.hideAllSelectors();
    },
    toggleYearSelector() {
      this.showingYears = !this.showingYears;
    },
    getWeeksDifference(startMoment, endMoment) {
      const s = this.cloneMoment(startMoment).startOf('isoWeek');
      const e = this.cloneMoment(endMoment).startOf('isoWeek');
      if (window.moment) {
        return Math.abs(e.diff(s, 'weeks')) + 1;
      } else {
        const d1 = this.toDateMoment(s);
        const d2 = this.toDateMoment(e);
        const oneWeek = 1000 * 60 * 60 * 24 * 7;
        const diffTime = Math.abs(d2.getTime() - d1.getTime());
        return Math.ceil(diffTime / oneWeek) + 1;
      }
    },
    handleWeekSelection(weekStartMoment) {
      const selectedWeekStartMoment = this.startOfMoment(this.cloneMoment(weekStartMoment), 'week');
      const selectedWeekEndMoment = this.endOfMoment(this.cloneMoment(weekStartMoment), 'week');
      if (this.range) {
        if (!this.selectingRange || this.endDate) {
          this.startDate = selectedWeekStartMoment;
          this.endDate = null;
          this.selectingRange = true;
          this.$emit('range-start-selected', {
            startDate: this.toDateMoment(this.startDate),
            weekNumber: this.getWeekMoment(this.startDate),
            year: this.getYearMoment(this.startDate),
          });
        } else {
          let tempStartDate = this.startDate;
          let tempEndDate = selectedWeekEndMoment;
          if (this.isAfterMoment(tempStartDate, tempEndDate, 'week')) {
            [tempStartDate, tempEndDate] = [tempEndDate, tempStartDate];
          }
          if (this.maxRangeWeeks !== null) {
            const weeksInSelection = this.getWeeksDifference(tempStartDate, tempEndDate);
            if (weeksInSelection > this.maxRangeWeeks) {
              this.$emit('range-limit-exceeded', {
                selectedWeeks: weeksInSelection,
                maxAllowed: this.maxRangeWeeks,
              });
              return;
            }
          }
          this.startDate = tempStartDate;
          this.endDate = tempEndDate;
          this.selectingRange = false;
          this.$emit('range-selected', {
            startDate: this.toDateMoment(this.startDate),
            endDate: this.toDateMoment(this.endDate),
            startWeekNumber: this.getWeekMoment(this.startDate),
            endWeekNumber: this.getWeekMoment(this.endDate),
            startYear: this.getYearMoment(this.startDate),
            endYear: this.getYearMoment(this.endDate),
          });
          this.$emit('input', [
            this.toDateMoment(this.startDate),
            this.toDateMoment(this.endDate),
          ]);
        }
      } else {
        this.startDate = selectedWeekStartMoment;
        this.selectedWeek = selectedWeekStartMoment;
        this.$emit('input', this.toDateMoment(this.selectedWeek));
        this.$emit('week-selected', {
          startDate: this.toDateMoment(selectedWeekStartMoment),
          endDate: this.toDateMoment(selectedWeekEndMoment),
          weekNumber: this.getWeekMoment(this.selectedWeek),
          year: this.getYearMoment(this.selectedWeek),
        });
      }
      this.hideAllSelectors();
    },
    handleYearSelection(year) {
      this.currentDate = this.setYearMoment(this.currentDate, year);
      this.hideAllSelectors();
    },
    hideAllSelectors() {
      this.showingYears = false;
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(newValue) {
        if (this.range) {
          if (Array.isArray(newValue) && newValue.length === 2 && newValue[0] && newValue[1]) {
            this.startDate = this.getMoment(newValue[0]);
            this.endDate = this.getMoment(newValue[1]);
            this.currentDate = this.cloneMoment(this.startDate);
            this.selectingRange = false;
          } else if (newValue && !Array.isArray(newValue)) {
            this.startDate = this.getMoment(newValue);
            this.endDate = null;
            this.currentDate = this.cloneMoment(this.startDate);
            this.selectingRange = true;
          } else {
            this.startDate = null;
            this.endDate = null;
            this.selectingRange = false;
            this.currentDate = this.getMoment();
          }
        } else {
          if (newValue) {
            this.startDate = this.getMoment(newValue);
            this.endDate = null;
            this.currentDate = this.cloneMoment(this.startDate);
          } else {
            this.startDate = null;
            this.endDate = null;
            this.currentDate = this.getMoment();
          }
        }
      },
    },
  },
};
</script>

<style lang="scss" scoped>
$small-scale-factor: 0.8;
$base-font-size: 20px;

.calendar {
  background: white;
  font-family: inherit;
  font-size: $base-font-size;
  font-weight: 400;
  color: #09090b;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  &-wrapper {
    position: relative;
    flex-grow: 1;
    max-height: 300px;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }

  &--small {
    font-size: $base-font-size * $small-scale-factor;
    width: 273px;
  }

  .fade-enter-active, .fade-leave-active {
    transition: opacity 0.15s ease;
  }

  .fade-enter, .fade-leave-to {
    opacity: 0;
  }
}

.weeks-grid {
  &__alignment {
    padding: 0;
  }
}

.years-grid {
  &__alignment {
    padding: 0;
  }
}
</style>
